import { useState } from 'react';
import conversationService from '../services/conversationService';
import useNotification from './useNotification';

/**
 * Custom hook for managing explanation functionality
 * Handles explanation requests, modal state, and API calls
 */
const useExplain = (projectId) => {
  const [isExplaining, setIsExplaining] = useState(false);
  const [explanationContent, setExplanationContent] = useState('');
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  
  const { showNotification } = useNotification();

  // Request explanation for a section
  const requestExplanation = async (section) => {
    try {
      // Set the current section and show modal immediately with loading state
      setCurrentSection(section);
      setExplanationContent(''); // Clear any previous content
      setShowExplanationModal(true); // Show modal immediately
      setIsExplaining(true); // Show loading state
      
      // Prepare the request payload
      const requestData = {
        projectId,
        sectionTitle: section.title,
        sectionContent: section.content || section.rawContent || '',
        sectionType: 'feature'
      };

      // Call explanation API
      const response = await conversationService.requestExplanation(requestData);
      
      if (response && response.explanation) {
        setExplanationContent(response.explanation);
        setIsExplaining(false); // Stop loading
      } else {
        throw new Error('No explanation received');
      }
      
    } catch (error) {
      console.error('Error requesting explanation:', error);
      setShowExplanationModal(false); // Close modal on error
      showNotification({
        message: 'Failed to generate explanation',
        type: 'error'
      });
      setIsExplaining(false);
    }
  };

  // Close explanation modal
  const closeExplanationModal = () => {
    setShowExplanationModal(false);
    setExplanationContent('');
    setCurrentSection(null);
  };

  return {
    // State
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection,
    
    // Actions
    requestExplanation,
    closeExplanationModal
  };
};

export default useExplain;